/**
 * Manifest loader with gzip support
 * 
 * Loads manifest.json.gz (compressed, 4x smaller) with automatic fallback
 * to manifest.json (uncompressed) for backward compatibility.
 */

import { toBlobUrl, isBlobUrl } from '@/config'

/**
 * Load manifest with automatic .gz detection and fallback
 * 
 * Try sequence:
 * 1. manifest.json.gz (compressed, preferred)
 * 2. manifest.json (uncompressed, fallback)
 * 
 * Browser automatically decompresses .gz files, so no extra decompression needed.
 * 
 * @param manifestPath - Path to manifest.json (e.g., /reconstructions/shop/section_0/manifest.json)
 * @returns Parsed manifest object or null if not found
 */
export async function loadManifestWithFallback(
  manifestPath: string
): Promise<any | null> {
  // For blob URLs, SAS provides auth; for API proxy, use bearer token
  const useBlobAuth = isBlobUrl(manifestPath)
  const authToken = localStorage.getItem('dukkan_token')
  const headers: Record<string, string> = {}
  if (!useBlobAuth && authToken) headers['Authorization'] = `Bearer ${authToken}`

  // Try compressed version first
  try {
    const gzPath = manifestPath.replace('.json', '.json.gz')
    const gzResponse = await fetch(toBlobUrl(gzPath), { headers })
    if (gzResponse.ok) {
      const data = await gzResponse.json()
      console.log(`[manifestLoader] Loaded from ${gzPath.split('/').pop()}`)
      return data
    }
  } catch (error) {
    // Fall through to uncompressed
  }

  // Fallback to uncompressed
  try {
    const response = await fetch(toBlobUrl(manifestPath), { headers })
    if (response.ok) {
      const data = await response.json()
      console.log(`[manifestLoader] Loaded from ${manifestPath.split('/').pop()}`)
      return data
    }
  } catch (error) {
    // Both failed
  }

  return null
}
